import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2 } from 'lucide-react';
import StripeCheckoutModal from '../components/StripeCheckoutModal';

export default function Subscription() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [needsSubscription, setNeedsSubscription] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [hasTriggeredSubscribe, setHasTriggeredSubscribe] = useState(false);
  const navigate = useNavigate();
  
  // Check if user needs to subscribe and automatically open the payment modal
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          navigate('/');
          return;
        }
        
        // Get user profile
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('created_at, is_subscribed')
          .eq('id', user.id)
          .single();
          
        if (error) throw error;
        
        // Check if user signed up today or later (after May 22, 2025)
        // And check if they are not subscribed
        if (profile) {
          const createdAt = profile.created_at ? new Date(profile.created_at) : null;
          const cutoffDate = new Date('2025-05-23T15:10:54+05:00'); // Current timestamp
          
          // If user signed up today or later and is not subscribed, they need to subscribe
          if (createdAt && createdAt >= cutoffDate && profile.is_subscribed === false) {
            setNeedsSubscription(true);
          } else {
            // User doesn't need to subscribe, redirect to community
            navigate('/community');
          }
        } else {
          // If profile not found, something is wrong
          throw new Error('User profile not found');
        }
      } catch (err) {
        console.error('Error checking subscription status:', err);
        setError('Error checking subscription status');
      } finally {
        setCheckingStatus(false);
      }
    };
    
    checkSubscriptionStatus();
  }, [navigate]);
  
  // Automatically trigger the subscription process when needed
  useEffect(() => {
    if (needsSubscription && !checkingStatus && !hasTriggeredSubscribe && !loading && !error) {
      setHasTriggeredSubscribe(true);
      handleSubscribe();
    }
  }, [needsSubscription, checkingStatus, hasTriggeredSubscribe, loading, error]);
  
  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('You must be logged in to subscribe');
      }
      
      // Create a subscription checkout session through our Edge Function
      const { data, error } = await supabase.functions.invoke('create-payment-link', {
        body: { 
          user_id: user.id,
          email: user.email
        }
      });
      
      if (error) throw error;
      
      // Check if we got a checkout URL back
      if (data?.url) {
        // Redirect to Stripe's hosted checkout page
        window.location.href = data.url;
      } else if (data?.clientSecret) {
        // Store the client secret and open the modal
        setClientSecret(data.clientSecret);
        setIsModalOpen(true);
      } else {
        throw new Error('Failed to create checkout session');
      }
      
    } catch (err) {
      console.error('Subscription error:', err);
      if (typeof err === 'object' && err !== null && 'message' in err) {
        setError((err as any).message);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setClientSecret(null);
    
    // Reset the state to trigger the payment flow again
    setTimeout(() => {
      setHasTriggeredSubscribe(false);
      // This will cause the useEffect to trigger handleSubscribe again
    }, 1000); // Short delay before reopening
  };
  
  const handlePaymentSuccess = () => {
    // After successful payment, close modal and redirect to community page
    setIsModalOpen(false);
    setClientSecret(null);
    navigate('/community?payment_success=true');
  };
  
  // Display loading screen while checking status
  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <Loader2 className="w-6 h-6 text-[#c9fffc] animate-spin" />
          <div className="text-white">Checking subscription status...</div>
        </div>
      </div>
    );
  }
  
  // Only show the subscription page if the user actually needs to subscribe
  return needsSubscription ? (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 bg-gray-900">
      <div className="text-center">
        {(loading || (!clientSecret && !error && !isModalOpen) || (hasTriggeredSubscribe && !isModalOpen && !error)) && (
          <div className="flex flex-col items-center">
            <Loader2 className="w-10 h-10 text-[#c9fffc] animate-spin mb-4" />
            <p className="text-white text-lg">Preparing your subscription...</p>
            <p className="text-gray-400 text-sm mt-2">Please complete the payment to continue</p>
          </div>
        )}
        
        {error && (
          <div className="max-w-md">
            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
              {error}
            </div>
            <div className="flex flex-col gap-4">
              <button
                onClick={() => {
                  setError(null);
                  setHasTriggeredSubscribe(false);
                  handleSubscribe();
                }}
                className="px-6 py-3 bg-[#c9fffc] text-gray-900 rounded-xl hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] transition-all font-medium"
              >
                Try Again
              </button>
              
              <button
                onClick={() => supabase.auth.signOut().then(() => navigate('/'))}
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Cancel and sign out
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Stripe Checkout Modal */}
      <StripeCheckoutModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        clientSecret={clientSecret}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  ) : (
    // This fallback should never be shown as we redirect non-subscription users
    // But we need it to complete the ternary expression
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-white">Redirecting to community...</div>
    </div>
  );
}
